import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  InputGroup,
  Form,
  Pagination,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetWorksQuery,
  useToggleWorkStatusMutation,
  useDeleteWorkMutation,
} from "../../../redux/api/WorksApi/worksApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaBriefcase,
  FaCheckCircle,
  FaTrash,
} from "react-icons/fa";
import Swal from "sweetalert2";

const Works = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const sortBy = searchParams.get("sortBy") || "display_order";
  const order = searchParams.get("order") || "asc";
  const limit = Number(searchParams.get("limit")) || 10;

  const [searchTerm, setSearchTerm] = useState(search);

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== search) {
        setSearchParams({
          page: 1,
          limit,
          search: searchTerm,
          sortBy,
          order,
        });
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm, limit, sortBy, order, search, setSearchParams]);

  const { data, isLoading } = useGetWorksQuery({
    page,
    limit,
    search,
    sortBy,
    order,
  });

  const [toggleStatus] = useToggleWorkStatusMutation();
  const [deleteWork] = useDeleteWorkMutation();

  const works = data?.works || [];
  const totalPages = Math.ceil((data?.total || 0) / limit);

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  const handleToggle = async (id) => {
    try {
      await toggleStatus(id).unwrap();
    } catch (error) {
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteWork(id).unwrap();
        Swal.fire("Deleted!", "Work has been deleted.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete work", "error");
      }
    }
  };

  const activeCount = works.filter((s) => s.status).length;

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaBriefcase className="text-primary me-2" />
            Works Management
          </h2>
          <p className="text-muted mb-0">
            Manage your project showcase and portfolio
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate("/admin/add-work")}
          className="d-flex align-items-center gap-2 px-4 py-2 shadow-sm rounded-pill fw-semibold"
        >
          <FaPlus /> Add New Work
        </Button>
      </div>

      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100">
            <div className="d-flex align-items-center">
              <div className="bg-primary-soft text-primary p-3 rounded-circle me-3">
                <FaBriefcase size={20} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">{data?.total || 0}</h4>
                <div className="text-muted small fw-semibold">
                  Total Projects
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100">
            <div className="d-flex align-items-center">
              <div className="bg-success-soft text-success p-3 rounded-circle me-3">
                <FaCheckCircle size={20} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">{activeCount}</h4>
                <div className="text-muted small fw-semibold">
                  Active Projects
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
        <Card.Header className="bg-white border-bottom p-3">
          <Row className="align-items-center">
            <Col md={3}>
              <Form.Select
                size="sm"
                className="w-auto"
                value={limit}
                onChange={(e) =>
                  setSearchParams({
                    page: 1,
                    limit: Number(e.target.value),
                    search,
                    sortBy,
                    order,
                  })
                }
              >
                <option value={10}>Show 10</option>
                <option value={25}>Show 25</option>
                <option value={50}>Show 50</option>
              </Form.Select>
            </Col>
            <Col md={9}>
              <InputGroup className="bg-light rounded-pill border-0 px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search projects..."
                  className="bg-transparent border-0 shadow-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <div className="table-responsive">
          <Table hover className="align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4">Project</th>
                <th>Slug</th>
                <th className="text-center">Order</th>
                <th className="text-center">Status</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {works.map((w) => (
                <tr key={w.id}>
                  <td className="ps-4 py-3">
                    <div className="fw-bold text-dark">{w.title}</div>
                    <div className="text-muted extra-small">{w.subtitle}</div>
                  </td>
                  <td>
                    <code className="bg-light px-2 py-1 rounded text-primary small">
                      {w.slug}
                    </code>
                  </td>
                  <td className="text-center">
                    <Badge bg="secondary" pill>
                      {w.display_order}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <Badge
                      bg={w.status ? "success-light" : "danger-light"}
                      className={`${w.status ? "text-success" : "text-danger"} border-0 px-3 py-2 rounded-pill cursor-pointer`}
                      onClick={() => handleToggle(w.id)}
                    >
                      {w.status ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="white"
                        size="sm"
                        onClick={() => navigate(`/admin/edit-work/${w.id}`)}
                        className="shadow-sm border"
                      >
                        <FaEdit className="text-primary" />
                      </Button>
                      <Button
                        variant="white"
                        size="sm"
                        onClick={() => handleDelete(w.id)}
                        className="shadow-sm border"
                      >
                        <FaTrash className="text-danger" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {works.length === 0 && !isLoading && (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted">
                    No projects found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {totalPages > 1 && (
        <Pagination className="justify-content-center my-4">
          <Pagination.Prev
            disabled={page === 1}
            onClick={() =>
              setSearchParams({ page: page - 1, limit, search, sortBy, order })
            }
          />
          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i + 1}
              active={page === i + 1}
              onClick={() =>
                setSearchParams({ page: i + 1, limit, search, sortBy, order })
              }
            >
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page === totalPages}
            onClick={() =>
              setSearchParams({ page: page + 1, limit, search, sortBy, order })
            }
          />
        </Pagination>
      )}
    </div>
  );
};

export default Works;
